'use client';

import { buildAnalyticsAttributionProperties } from '@/analytics/analytics-attribution';
import { captureAnalyticsClientEvent } from '@/analytics/analytics-client';
import {
  type EnhancerConversionContext,
  clearEnhancerConversionContext,
  readEnhancerConversionContext,
} from '@/analytics/enhancer-conversion-context';
import type { WorkspaceTaskBlockPaymentContext } from '@/cms/components/blocks/workspace/workspace-1/contracts/task-block-payment';
import {
  type CheckoutAnalyticsAttribution,
  type CheckoutAnalyticsInput,
  createCheckoutConversionId,
} from '@/payment/attribution/checkout-analytics-context';
import type { PaymentProviderId } from '@/payment/provider-list';
import {
  type CheckoutErrorCode,
  checkoutErrorCodes,
} from '@/payment/services/checkout/types';
import { createWorkspaceTaskClientEventId } from '@/payment/task/task-payment-client';
import type { AdminV2PaywallPopupBranchSlug } from '@/site-runtime/code-owned/paywall-branches';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type SiteRuntimeCheckoutSurface,
  buildSiteRuntimeCheckoutLoginCallback,
  buildSiteRuntimePaymentCallback,
  readSiteRuntimeCheckoutLoginIntent,
  removeSiteRuntimeCheckoutLoginIntentFromBrowserUrl,
} from './site-runtime-checkout-login-intent';
import {
  getCurrentSiteRuntimeCheckoutCallback,
  resolveSiteRuntimeCheckoutReturnPath,
} from './site-runtime-checkout-return-path';

function isCheckoutErrorCode(value: unknown): value is CheckoutErrorCode {
  return (
    typeof value === 'string' &&
    (checkoutErrorCodes as readonly string[]).includes(value)
  );
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function buildCheckoutAttribution(
  properties: Record<string, unknown>
): CheckoutAnalyticsAttribution {
  return {
    firstTouchSource: readOptionalString(properties.first_touch_source),
    firstTouchMedium: readOptionalString(properties.first_touch_medium),
    firstTouchCampaign: readOptionalString(properties.first_touch_campaign),
    firstTouchContent: readOptionalString(properties.first_touch_content),
    firstTouchTerm: readOptionalString(properties.first_touch_term),
    firstTouchLandingPage: readOptionalString(
      properties.first_touch_landing_page
    ),
    firstTouchClickId: readOptionalString(properties.first_touch_click_id),
    lastTouchSource: readOptionalString(properties.last_touch_source),
    lastTouchMedium: readOptionalString(properties.last_touch_medium),
    lastTouchCampaign: readOptionalString(properties.last_touch_campaign),
    lastTouchContent: readOptionalString(properties.last_touch_content),
    lastTouchTerm: readOptionalString(properties.last_touch_term),
    lastTouchLandingPage: readOptionalString(
      properties.last_touch_landing_page
    ),
    lastTouchClickId: readOptionalString(properties.last_touch_click_id),
  };
}

function captureCheckoutEventOnce(
  key: string,
  event: string,
  properties: Record<string, unknown>
) {
  const storageKey = `imgenhancer:analytics:${key}`;
  try {
    if (window.sessionStorage.getItem(storageKey)) return;
    window.sessionStorage.setItem(storageKey, '1');
  } catch {
    // Analytics deduplication is best-effort when browser storage is blocked.
  }
  captureAnalyticsClientEvent(event, properties);
}

export function useSiteRuntimeHostedCheckout(params: {
  checkoutIntentKey: string;
  checkoutSurface: SiteRuntimeCheckoutSurface;
  getCheckoutErrorMessage: (errorCode: CheckoutErrorCode) => string;
  locale: string;
  conversionId?: string;
  onCheckoutCreated?: () => void;
  paywallBranchSlug?: AdminV2PaywallPopupBranchSlug;
  onCheckoutError: (errorCode: CheckoutErrorCode) => void;
  onUnauthenticated: (loginCallbackUrl: string) => void;
  resumeLoginIntent?: boolean;
  taskBlockPayment?: WorkspaceTaskBlockPaymentContext;
}) {
  const [activeProvider, setActiveProvider] =
    useState<PaymentProviderId | null>(null);
  const [initialLoginIntent] = useState(() =>
    params.resumeLoginIntent && typeof window !== 'undefined'
      ? readSiteRuntimeCheckoutLoginIntent(window.location.href)
      : null
  );
  const [conversionId] = useState(
    () =>
      params.conversionId ??
      initialLoginIntent?.conversionId ??
      createCheckoutConversionId()
  );
  const paywallBranchSlug =
    params.paywallBranchSlug ?? initialLoginIntent?.paywallBranchSlug;
  const [enhancerConversionContext, setEnhancerConversionContext] =
    useState<EnhancerConversionContext | null>(null);
  const [conversionContextLoaded, setConversionContextLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const resumedLoginIntentRef = useRef<string | null>(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    setEnhancerConversionContext(readEnhancerConversionContext());
    setConversionContextLoaded(true);
  }, []);

  const currentPageCallback = useMemo(
    () => getCurrentSiteRuntimeCheckoutCallback(),
    []
  );
  const paymentCallback = useMemo(
    () =>
      buildSiteRuntimePaymentCallback(resolveSiteRuntimeCheckoutReturnPath()),
    []
  );
  const pagePath =
    typeof window === 'undefined' ? '' : window.location.pathname;
  const touchAttributionProperties = useMemo(() => {
    if (typeof window === 'undefined') {
      return buildAnalyticsAttributionProperties();
    }
    return buildAnalyticsAttributionProperties({
      fallbackSearchParams: new URLSearchParams(window.location.search),
      fallbackReferrer: document.referrer || null,
    });
  }, [pagePath]);

  const buildCheckoutEventProperties = useCallback(
    (details?: {
      provider?: PaymentProviderId;
      reasonCode?: CheckoutErrorCode;
      reasonMessage?: string;
    }) => ({
      checkout_intent_key: params.checkoutIntentKey,
      checkout_surface: params.checkoutSurface,
      conversion_id: conversionId,
      paywall_branch_slug: paywallBranchSlug ?? null,
      callback: paymentCallback,
      locale: params.locale,
      page_path: pagePath,
      provider: details?.provider,
      reason_code: details?.reasonCode,
      reason_message: details?.reasonMessage,
      conversion_source: enhancerConversionContext?.conversion_source ?? null,
      enhancer_session_id:
        enhancerConversionContext?.enhancer_session_id ?? null,
      enhancer_tool_id: enhancerConversionContext?.enhancer_tool_id ?? null,
      enhancer_entry_surface:
        enhancerConversionContext?.enhancer_entry_surface ?? null,
      source_ui_surface: enhancerConversionContext?.source_ui_surface ?? null,
      ...touchAttributionProperties,
    }),
    [
      conversionId,
      enhancerConversionContext,
      pagePath,
      params.checkoutIntentKey,
      params.checkoutSurface,
      params.locale,
      paywallBranchSlug,
      paymentCallback,
      touchAttributionProperties,
    ]
  );

  const trackCheckoutEvent = useCallback(
    (
      event:
        | 'checkout_failed'
        | 'checkout_initiated'
        | 'checkout_login_required'
        | 'checkout_redirected',
      details?: {
        provider?: PaymentProviderId;
        reasonCode?: CheckoutErrorCode;
        reasonMessage?: string;
      }
    ) =>
      captureAnalyticsClientEvent(event, buildCheckoutEventProperties(details)),
    [buildCheckoutEventProperties]
  );

  const startCheckout = useCallback(
    async (provider?: PaymentProviderId) => {
      if (submittingRef.current) {
        return;
      }

      submittingRef.current = true;
      setActiveProvider(provider ?? null);
      setSubmitting(true);
      const checkoutEventProperties = buildCheckoutEventProperties({
        provider,
      });
      captureCheckoutEventOnce(
        `begin_checkout:${conversionId}`,
        'begin_checkout',
        {
          ...checkoutEventProperties,
          items: [
            {
              item_id: params.checkoutIntentKey,
              item_name: params.checkoutIntentKey,
            },
          ],
        }
      );
      if (provider) {
        captureCheckoutEventOnce(
          `add_payment_info:${conversionId}:${provider}`,
          'add_payment_info',
          {
            ...checkoutEventProperties,
            payment_type: provider,
            items: [
              {
                item_id: params.checkoutIntentKey,
                item_name: params.checkoutIntentKey,
              },
            ],
          }
        );
      }
      trackCheckoutEvent('checkout_initiated', { provider });

      try {
        // The task context is only attached when the paywall's server quote
        // actually resolved; a missing quote stays an ordinary catalog
        // checkout instead of fabricating pending-run identifiers.
        const taskQuote = params.taskBlockPayment
          ? await params.taskBlockPayment.quote
          : null;
        const response = await fetch(
          '/api/payment/checkout/admin-v2-runtime/start',
          {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              checkoutIntentKey: params.checkoutIntentKey,
              locale: params.locale,
              callback: paymentCallback,
              analytics: {
                conversionId,
                checkoutSurface: params.checkoutSurface,
                ...(paywallBranchSlug ? { paywallBranchSlug } : {}),
                ...(enhancerConversionContext?.enhancer_session_id
                  ? {
                      enhancerSessionId:
                        enhancerConversionContext.enhancer_session_id,
                    }
                  : {}),
                ...(enhancerConversionContext?.enhancer_tool_id
                  ? {
                      enhancerToolId:
                        enhancerConversionContext.enhancer_tool_id,
                    }
                  : {}),
                ...(enhancerConversionContext?.enhancer_entry_surface
                  ? {
                      enhancerEntrySurface:
                        enhancerConversionContext.enhancer_entry_surface,
                    }
                  : {}),
                ...(enhancerConversionContext?.source_ui_surface
                  ? {
                      sourceUiSurface:
                        enhancerConversionContext.source_ui_surface,
                    }
                  : {}),
                attribution: buildCheckoutAttribution(
                  touchAttributionProperties
                ),
              } satisfies CheckoutAnalyticsInput,
              ...(provider ? { provider } : {}),
              ...(taskQuote
                ? {
                    taskClickEventId: createWorkspaceTaskClientEventId(),
                    taskContext: {
                      pendingRunId: taskQuote.pendingRunId,
                      quoteId: taskQuote.quoteId,
                    },
                  }
                : {}),
            }),
          }
        );

        if (response.status === 401) {
          trackCheckoutEvent('checkout_login_required', { provider });
          params.onUnauthenticated(
            buildSiteRuntimeCheckoutLoginCallback({
              callbackUrl: currentPageCallback,
              checkoutIntentKey: params.checkoutIntentKey,
              checkoutSurface: params.checkoutSurface,
              conversionId,
              paywallBranchSlug,
              provider,
            })
          );
          return;
        }

        const payload = (await response.json()) as {
          success?: boolean;
          data?: { checkoutUrl?: string };
          errorCode?: unknown;
        };
        if (payload.success && payload.data?.checkoutUrl) {
          trackCheckoutEvent('checkout_redirected', { provider });
          if (enhancerConversionContext?.conversion_source === 'enhancer') {
            clearEnhancerConversionContext();
          }
          params.onCheckoutCreated?.();
          window.location.assign(payload.data.checkoutUrl);
          return;
        }

        const errorCode = isCheckoutErrorCode(payload.errorCode)
          ? payload.errorCode
          : 'checkout_create_failed';
        const reasonMessage = params.getCheckoutErrorMessage(errorCode);
        trackCheckoutEvent('checkout_failed', {
          provider,
          reasonCode: errorCode,
          reasonMessage,
        });
        params.onCheckoutError(errorCode);
      } catch {
        const errorCode = 'checkout_create_failed';
        trackCheckoutEvent('checkout_failed', {
          provider,
          reasonCode: errorCode,
          reasonMessage: params.getCheckoutErrorMessage(errorCode),
        });
        params.onCheckoutError(errorCode);
      } finally {
        submittingRef.current = false;
        setActiveProvider(null);
        setSubmitting(false);
      }
    },
    [
      enhancerConversionContext,
      buildCheckoutEventProperties,
      conversionId,
      currentPageCallback,
      paymentCallback,
      params.checkoutIntentKey,
      params.checkoutSurface,
      params.getCheckoutErrorMessage,
      params.locale,
      paywallBranchSlug,
      params.onCheckoutError,
      params.onUnauthenticated,
      params.onCheckoutCreated,
      params.taskBlockPayment,
      trackCheckoutEvent,
    ]
  );

  useEffect(() => {
    if (!params.resumeLoginIntent || !conversionContextLoaded || submitting) {
      return;
    }

    const intent =
      initialLoginIntent ??
      readSiteRuntimeCheckoutLoginIntent(window.location.href);
    if (
      !intent ||
      intent.checkoutIntentKey !== params.checkoutIntentKey ||
      intent.checkoutSurface !== params.checkoutSurface
    ) {
      return;
    }

    const resumeKey = `${intent.conversionId}:${intent.checkoutIntentKey}:${
      intent.provider ?? ''
    }:${intent.checkoutSurface}`;
    if (resumedLoginIntentRef.current === resumeKey) {
      return;
    }

    resumedLoginIntentRef.current = resumeKey;
    removeSiteRuntimeCheckoutLoginIntentFromBrowserUrl();
    void startCheckout(intent.provider);
  }, [
    conversionContextLoaded,
    initialLoginIntent,
    params.checkoutIntentKey,
    params.checkoutSurface,
    params.resumeLoginIntent,
    startCheckout,
    submitting,
  ]);

  return {
    activeProvider,
    conversionId,
    startCheckout,
    submitting,
  };
}
